import { ApiResponse } from 'apisauce';
import { IPaginationSnapshot } from 'src/models/pagination/Pagination';
import { IResponseSnapshot } from 'src/models/response/Response';

import {
  ICalendar,
  IComment,
  IProject,
  IProjectFilter,
  IProjectHistory,
  IProjectSnapshot,
} from '../../models';
import { Api } from '../api/api';
import { getGeneralApiProblem } from '../api/api-problem';

import {
  TDeleteCommentsResult,
  TDeleteProjectResult,
  TFileDownloadResult,
  TGetCalendarResult,
  TGetProjectHistoryResult,
  TGetProjectResult,
  TGetProjectsResult,
  TPostCommentsResult,
  TPostProjectResult,
  TUpdateProjectResult,
} from './ProjectTypes';

/**
 * # Project Api 서비스
 *
 * 서비스의 설명을 작성하세요.
 *
 * ## 사용방법
 *
 * ```ts
 * const service = new ProjectApi();
 *
 * ```
 */

function buildFormData(formData: [string, any][], data: any, parentKey?: string): void {
  if (data instanceof File || data instanceof Blob) {
    // 파일 객체 처리
    formData.push([parentKey || '', data]);
  } else if (data && typeof data === 'object' && !(data instanceof Date)) {
    Object.keys(data).forEach((key) => {
      // 제외할 키 처리
      if (
        key === 'value' ||
        key === 'pcode' ||
        key === 'regDt' ||
        key === 'updDt' ||
        key.includes('regUser') ||
        key.includes('updUser') ||
        key === 'delYn'
      ) {
        return;
      }

      // 키에 따라 다른 처리
      if (key === 'code') {
        buildFormData(formData, data[key], parentKey ? `${parentKey}` : key);
      } else if (!isNaN(Number(key))) {
        buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
      } else {
        buildFormData(formData, data[key], parentKey ? `${parentKey}.${key}` : key);
      }
    });
  } else {
    // 값이 null 또는 빈 문자열이 아니거나 값이 숫자 0인 경우 처리
    if ((data != null && data !== '') || data === 0) {
      const value = data == null ? '' : data;
      formData.push([parentKey || '', value]);
    }
  }
}

export class ProjectApi {
  private api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  async gets(paging: IPaginationSnapshot, params?: IProjectFilter): Promise<TGetProjectsResult> {
    try {
      let url;

      if (params) {
        // @ts-ignore
        const queryParams = new URLSearchParams(params).toString();
        url = `/v1/project?${queryParams}`; // &page=${paging.page}`; // &size=${paging.size}`;
      } else {
        url = `/v1/project?page=${paging.page}&size=${paging.size}`;
      }
      const response: ApiResponse<any> = await this.api.apisauce.get(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const projects: IProject[] = response.data.data;
      const pagination: IPaginationSnapshot = response.data.pagination;
      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', projects, pagination, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async get(projectSid: number): Promise<TGetProjectResult> {
    try {
      const url = `/v1/project/${projectSid}`;
      const params = {};
      const response: ApiResponse<any> = await this.api.apisauce.get(url, params);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const project: IProject = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', project, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async getHistory(projectSid: number): Promise<TGetProjectHistoryResult> {
    try {
      const url = `/v1/project/${projectSid}/history`;
      const response: ApiResponse<any> = await this.api.apisauce.get(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }
      const projectHistory: IProjectHistory = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', projectHistory, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async fileDownload(projectSid: number, filePath: string): Promise<TFileDownloadResult> {
    try {
      const url = `/v1/project/${projectSid}/download?filePath=${filePath}`;
      const response: ApiResponse<any> = await this.api.apisauce.get(url);
      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  // async getComments(projectSid: number): Promise<TGetCommentsResult> {
  //   try {
  //     const url = `/v1/project/${projectSid}/comment`;
  //     const response: ApiResponse<any> = await this.api.apisauce.get(url);

  //     if (!response.ok) {
  //       const problem = getGeneralApiProblem(response);
  //       if (problem) return problem;
  //     }

  //     const comments: IComment[] = response.data.data;
  //     const responseInfo: IResponseSnapshot = response.data;

  //     return { kind: 'ok', comments, responseInfo };
  //   } catch (e) {
  //     return { kind: 'bad-data' };
  //   }
  // }

  async postComment(projectSid: number, data: string): Promise<TPostCommentsResult> {
    try {
      const url = `/v1/project/${projectSid}/comment`;
      const params = { comment: data };
      const response: ApiResponse<any> = await this.api.apisauce.post(url, params);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const comment: IComment = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', comment, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async putComment(
    projectSid: number,
    commentSid: number,
    data: string,
  ): Promise<TPostCommentsResult> {
    try {
      const url = `/v1/project/${projectSid}/comment/${commentSid}`;
      const params = { comment: data };
      const response: ApiResponse<any> = await this.api.apisauce.put(url, params);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const comment: IComment = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', comment, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async deleteComment(projectSid: number, commentSid: number): Promise<TDeleteCommentsResult> {
    try {
      const url = `/v1/project/${projectSid}/comment/${commentSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.delete(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async post(newProject: IProjectSnapshot): Promise<TPostProjectResult> {
    try {
      const url = '/v1/project';
      const formData = new FormData();

      // for (const key in newProject) {
      //   const value = newProject[key as keyof IProjectSnapshot];

      //   // 값이 null 또는 undefined가 아닌 경우에만 추가
      //   if (value !== null && value !== undefined) {
      //     // 객체나 배열은 JSON 문자열로 변환
      //     if (typeof value === 'object') {
      //       formData.append(key, JSON.stringify(value));
      //     } else {
      //       formData.append(key, value.toString());
      //     }
      //   }
      // }
      // Use buildFormData to construct form data entries
      const dataEntries: [string, any][] = [];
      buildFormData(dataEntries, newProject);

      // Append entries to FormData
      dataEntries.forEach(([key, value]) => {
        if (key === 'quotationFile') {
          if (value instanceof File) {
            formData.append('quotationFile', value);
          } else if (value && Array.isArray(value) && value.length > 0) {
            (value as File[]).forEach((file) => {
              formData.append('quotationFile', file);
            });
          }
        } else {
          formData.append(key, value);
        }
      });

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };
      const response: ApiResponse<any> = await this.api.apisauce.post(url, formData, config);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const project: IProject = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', project, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async update(newProject: IProjectSnapshot): Promise<TUpdateProjectResult> {
    try {
      const url = `/v1/project/${newProject.projectSid}`;
      const formData = new FormData();

      // Use buildFormData to construct form data entries
      const dataEntries: [string, any][] = [];
      buildFormData(dataEntries, newProject);

      // Append entries to FormData
      dataEntries.forEach(([key, value]) => {
        if (key === 'quotationFile') {
          if (value instanceof File) {
            formData.append('quotationFile', value);
          } else if (value && Array.isArray(value) && value.length > 0) {
            (value as File[]).forEach((file) => {
              formData.append('quotationFile', file);
            });
          }
        } else {
          formData.append(key, value);
        }
      });

      // Debugging logs for FormData
      // formData.forEach((value, key) => {
      //   console.log('>>>> ', key, value);
      // });

      const config = {
        headers: { 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
      };
      const response: ApiResponse<any> = await this.api.apisauce.put(url, formData, config);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const project: IProject = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', project, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async delete(projectSid: number): Promise<TDeleteProjectResult> {
    try {
      const url = `/v1/project/${projectSid}`;
      const payload = {};
      const response: ApiResponse<any> = await this.api.apisauce.delete(url, payload);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async calendar(params?: IProjectFilter): Promise<TGetCalendarResult> {
    try {
      let url;
      if (params) {
        // @ts-ignore
        const queryParams = new URLSearchParams(params).toString();
        url = `/v1/calendar?${queryParams}`;
      } else {
        url = `/v1/calendar`;
      }
      const response: ApiResponse<any> = await this.api.apisauce.get(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const data: ICalendar[] = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;

      return { kind: 'ok', data, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
}
